import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {createRef, CSSProperties, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import Parameter from "../../parameter/Parameter";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {ColorPicker} from "primereact/colorpicker";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import PositionInput from "../../../../shared/components/PositionInput";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import FileInput, {File} from "../../../../shared/components/FileInput";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import WatermarkDialogFields from "../../field/page/WatermarkDialogFields";
import {InputSwitch} from "primereact/inputswitch";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import validator from "validator";
import PdfViewer, {PdfViewerDefinition, PreviewState} from "../../../viewer/pdf/PdfViewer";
import {PdfPageDefinition} from "../../../viewer/pdf/PdfPage";
import MimeType from "../../../document/mimetype/MimeType";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import PageRange from "../../../../shared/components/PageRange";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Environment,
	Functions,
	Information,
	MetadataRectangle,
	PagePages,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxWatermarkWatermark,
	WatermarkFont,
	WatermarkImage,
	WatermarkPosition,
	WatermarkPositionMode,
	WatermarkText
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {ViewerMode} from "../../../viewer/ViewerMode";
import {PortalToolboxWatermarkWatermark, WatermarkMode} from "../../../../shared/models/PortalToolboxWatermarkWatermark";
import {PortalWatermarkFileData} from "../../../../shared/models/PortalWatermarkFileData";
import Immutable from "../../../../shared/immutable/Immutable";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {SelectItem} from "primereact/selectitem";
import DropdownFilter from "../../../../shared/components/DropdownFilter";

interface WatermarkDialogProperties extends PortalDialogProperties<PortalToolboxWatermarkWatermark>,
	PortalDocumentManagerGlobals {
	userSettings: Immutable<UserSettings>;
}

interface WatermarkDialogStatus extends PortalDialogState<PortalToolboxWatermarkWatermark>, PreviewState {
	pages: number;
	currentPage: string;
	imageFile: File;
	imageWidth: number;
	imageHeight: number;
	unit: LengthUnit;
}

export class WatermarkDialogDefinition extends PortalDialog<PortalToolboxWatermarkWatermark,
	WatermarkDialogProperties, WatermarkDialogStatus> {
	private textPreview: RefObject<HTMLImageElement>;
	private imagePreview: RefObject<HTMLImageElement>;

	constructor(props: any) {
		super(props);

		this.textPreview = createRef<HTMLImageElement>();
		this.imagePreview = createRef<HTMLImageElement>();
		this.state = {
			...this.state,
			previewZoomValue: 0,
			renderPreview: false,
			pages: 1,
			currentPage: "1",
			imageFile: {
				name: "",
				mimetype: "image/png",
				value: "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
			},
			imageWidth: 1,
			imageHeight: 1,
			fieldErrors: new Map<WatermarkDialogFields, string>(),
			unit: LengthUnit.MILLIMETER
		};

		this.onChange = this.onChange.bind(this);
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.addWatermark, Functions.getAddWatermarkDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.WATERMARK, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["watermark-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}
					onShow={() => this.setState({
						renderPreview: true
					})}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.watermark.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
					{this.state.renderPreview && this.renderPreview()}
				</div>
			</Dialog>
		);
	}

	private renderTextPreview(): ReactNode {
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter) || parameter?.mode !== WatermarkMode.TEXT) {
			return null;
		}

		let previewScale: number = this.state.previewZoomValue / 100;

		let position: WatermarkPositionMode = FunctionHelper.applyValue(
			parameter.text?.position?.position, WatermarkPosition.getPositionDefault()
		);
		let fontFamily: string = FunctionHelper.applyValue(parameter.text?.font?.name, WatermarkFont.getNameDefault());
		let fontColor: string = FunctionHelper.applyValue(parameter.text?.font?.color, WatermarkFont.getColorDefault());
		let fontSize: string = Math.floor(
			FunctionHelper.applyValue(parameter.text?.font?.size, WatermarkFont.getSizeDefault()) * previewScale
		) + LengthUnit.POINTS;
		let textContent: string = FunctionHelper.applyValue(parameter.text?.text, WatermarkText.getTextDefault());
		let widthOffset: number = 0;
		let heightOffset: number = 0;

		let offsetLeft: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(
				parameter.text?.position?.x, WatermarkPosition.getXDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution
		) * previewScale;
		let offsetTop: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(
				parameter.text?.position?.y, WatermarkPosition.getYDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution
		) * previewScale;
		let customWidthOffset: number = 0;
		let customHeightOffset: number = 0;
		let aspectRatio: boolean = FunctionHelper.applyValue(
			parameter.text?.position?.aspectRatio, WatermarkPosition.getAspectRatioDefault()
		);

		if (position !== WatermarkPositionMode.Custom) {
			customWidthOffset = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.text?.position?.width, WatermarkPosition.getWidthDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale / 2;
			customHeightOffset = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.text?.position?.height, WatermarkPosition.getHeightDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale / 2;
		}

		if (this.textPreview.current !== null) {
			widthOffset = this.textPreview.current.offsetWidth / 2;
			heightOffset = this.textPreview.current.offsetHeight / 2;
		}

		let styleLeft: string = "0";
		switch (position) {
			case WatermarkPositionMode.Custom:
				styleLeft = (offsetLeft + customWidthOffset) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopLeft:
			case WatermarkPositionMode.CenterLeft:
			case WatermarkPositionMode.BottomLeft:
				styleLeft = (widthOffset + offsetLeft) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopCenter:
			case WatermarkPositionMode.CenterCenter:
			case WatermarkPositionMode.BottomCenter:
				styleLeft = "calc(50% + " + offsetLeft + LengthUnit.PIXEL + ")";
				break;
			case WatermarkPositionMode.TopRight:
			case WatermarkPositionMode.CenterRight:
			case WatermarkPositionMode.BottomRight:
				styleLeft = "calc(100% + " + (offsetLeft - widthOffset) + LengthUnit.PIXEL + ")";
				break;
		}

		let styleTop: string = "0";
		switch (position) {
			case WatermarkPositionMode.Custom:
				styleTop = (offsetTop + customHeightOffset) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopLeft:
			case WatermarkPositionMode.TopCenter:
			case WatermarkPositionMode.TopRight:
				styleTop = (heightOffset + offsetTop) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.CenterLeft:
			case WatermarkPositionMode.CenterCenter:
			case WatermarkPositionMode.CenterRight:
				styleTop = "calc(50% + " + offsetTop + LengthUnit.PIXEL + ")";
				break;
			case WatermarkPositionMode.BottomLeft:
			case WatermarkPositionMode.BottomCenter:
			case WatermarkPositionMode.BottomRight:
				styleTop = "calc(100% + " + (offsetTop - heightOffset) + LengthUnit.PIXEL + ")";
				break;
		}

		let textStyle: CSSProperties = {
			opacity: FunctionHelper.applyValue(parameter.text?.font?.opacity, WatermarkFont.getOpacityDefault()) / 100,
			left: styleLeft,
			top: styleTop
		};

		if (position === WatermarkPositionMode.Custom) {
			textStyle.transformOrigin = "center center";
			textStyle.transform = "rotate(" + parameter.angle + "deg)";
		} else {
			textStyle.transform = "rotate(" + parameter.angle + "deg) translate(-50%, -50%)";
		}

		let canvas: HTMLCanvasElement = document.createElement("canvas");
		let context: CanvasRenderingContext2D | null = canvas.getContext("2d");
		if (FunctionHelper.exists(context)) {
			context!.font = fontSize + " " + fontFamily;
			let textMetrics: TextMetrics = context!.measureText(textContent);
			let textWidth: number = textMetrics.width;
			let textHeight: number =
				FunctionHelper.applyValue(textMetrics.fontBoundingBoxAscent, textMetrics.actualBoundingBoxAscent) +
				FunctionHelper.applyValue(textMetrics.fontBoundingBoxDescent, textMetrics.actualBoundingBoxDescent);

			if (position === WatermarkPositionMode.Custom) {
				let customWidth: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
					parameter.text?.position?.width, WatermarkPosition.getWidthDefault()
				), LengthUnit.MILLIMETER, this.props.portalResolution);
				let customHeight: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
					parameter.text?.position?.height, WatermarkPosition.getHeightDefault()
				), LengthUnit.MILLIMETER, this.props.portalResolution);
				let heightScale: number = customHeight / textHeight;
				let widthScale: number = customWidth / textWidth;

				if (aspectRatio) {
					if (heightScale < widthScale) {
						canvas.height = textHeight * heightScale * previewScale;
						canvas.width = textWidth * heightScale * previewScale;
						context!.scale(heightScale * previewScale, heightScale * previewScale);
					} else {
						canvas.height = textHeight * widthScale * previewScale;
						canvas.width = textWidth * widthScale * previewScale;
						context!.scale(widthScale * previewScale, widthScale * previewScale);
					}
				} else {
					canvas.width = customWidth * previewScale;
					canvas.height = customHeight * previewScale;
					context!.scale(widthScale * previewScale, heightScale * previewScale);
				}
			} else {
				canvas.width = textWidth;
				canvas.height = textHeight;
			}

			context!.fillStyle = fontColor;
			context!.font = fontSize + " " + fontFamily;
			context!.fillText(textContent, 0, textMetrics.actualBoundingBoxAscent);
		}

		return (
			<img hidden={parameter.mode !== WatermarkMode.TEXT} ref={this.textPreview} style={textStyle}
				 alt={Translate.value("watermark.preview.alt", {ns: "dialog"})}
				 src={canvas.toDataURL(MimeTypes.PNG.toString())}/>
		);
	}

	private renderImagePreview(): ReactNode {
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter) || parameter?.mode !== WatermarkMode.IMAGE) {
			return null;
		}

		let previewScale: number = this.state.previewZoomValue / 100;

		let position: WatermarkPositionMode = FunctionHelper.applyValue(
			parameter!.image?.position?.position, WatermarkPosition.getPositionDefault()
		);

		let widthOffset: number = this.state.imageWidth * (FunctionHelper.applyValue(
			parameter.image?.scale, WatermarkImage.getScaleDefault()
		) / 100 * previewScale) / 2;
		let heightOffset: number = this.state.imageHeight * (FunctionHelper.applyValue(
			parameter.image?.scale, WatermarkImage.getScaleDefault()
		) / 100 * previewScale) / 2;
		let offsetLeft: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
			parameter.image?.position?.x, WatermarkPosition.getXDefault()
		), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale;
		let offsetTop: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
			parameter.image?.position?.y, WatermarkPosition.getYDefault()
		), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale;
		let customWidthOffset: number = 0;
		let customHeightOffset: number = 0;
		let aspectRatio: boolean = FunctionHelper.applyValue(
			parameter.image?.position?.aspectRatio, WatermarkPosition.getAspectRatioDefault()
		);

		if (position !== WatermarkPositionMode.Custom) {
			customWidthOffset = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.image?.position?.width, WatermarkPosition.getWidthDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale / 2;
			customHeightOffset = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.image?.position?.height, WatermarkPosition.getHeightDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution) * previewScale / 2;
		}

		let styleLeft: string = "0";
		switch (position) {
			case WatermarkPositionMode.Custom:
				styleLeft = (offsetLeft + customWidthOffset) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopLeft:
			case WatermarkPositionMode.CenterLeft:
			case WatermarkPositionMode.BottomLeft:
				styleLeft = (widthOffset + offsetLeft) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopCenter:
			case WatermarkPositionMode.CenterCenter:
			case WatermarkPositionMode.BottomCenter:
				styleLeft = "calc(50% + " + offsetLeft + LengthUnit.PIXEL + ")";
				break;
			case WatermarkPositionMode.TopRight:
			case WatermarkPositionMode.CenterRight:
			case WatermarkPositionMode.BottomRight:
				styleLeft = "calc(100% + " + (offsetLeft - widthOffset) + LengthUnit.PIXEL + ")";
				break;
		}

		let styleTop: string = "0";
		switch (position) {
			case WatermarkPositionMode.Custom:
				styleTop = (offsetTop + customHeightOffset) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.TopLeft:
			case WatermarkPositionMode.TopCenter:
			case WatermarkPositionMode.TopRight:
				styleTop = (heightOffset + offsetTop) + LengthUnit.PIXEL;
				break;
			case WatermarkPositionMode.CenterLeft:
			case WatermarkPositionMode.CenterCenter:
			case WatermarkPositionMode.CenterRight:
				styleTop = "calc(50% + " + offsetTop + LengthUnit.PIXEL + ")";
				break;
			case WatermarkPositionMode.BottomLeft:
			case WatermarkPositionMode.BottomCenter:
			case WatermarkPositionMode.BottomRight:
				styleTop = "calc(100% + " + (offsetTop - heightOffset) + LengthUnit.PIXEL + ")";
				break;
		}

		let imageStyle: CSSProperties = {
			opacity: FunctionHelper.applyValue(
				parameter.image?.opacity, WatermarkImage.getOpacityDefault()
			) / 100,
			left: styleLeft,
			top: styleTop
		};

		if (position === WatermarkPositionMode.Custom) {
			let customWidth: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.image?.position?.width, WatermarkPosition.getWidthDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution);
			let customHeight: number = UnitHelper.unitToPx(FunctionHelper.applyValue(
				parameter.image?.position?.height, WatermarkPosition.getHeightDefault()
			), LengthUnit.MILLIMETER, this.props.portalResolution);

			if (aspectRatio) {
				let heightScale: number = customHeight / this.state.imageHeight;
				let widthScale: number = customWidth / this.state.imageWidth;

				if (heightScale < widthScale) {
					imageStyle.height = this.state.imageHeight * heightScale * previewScale;
					imageStyle.width = this.state.imageWidth * heightScale * previewScale;
				} else {
					imageStyle.height = this.state.imageHeight * widthScale * previewScale;
					imageStyle.width = this.state.imageWidth * widthScale * previewScale;
				}
			} else {
				imageStyle.width = customWidth * previewScale;
				imageStyle.height = customHeight * previewScale;
			}

			imageStyle.transformOrigin = "center center";
			imageStyle.transform = "rotate(" + parameter.angle + "deg)";
		} else {
			imageStyle.width = this.state.imageWidth * (FunctionHelper.applyValue(
				parameter.image?.scale, WatermarkImage.getScaleDefault()
			) / 100 * previewScale);
			imageStyle.height = this.state.imageHeight * (FunctionHelper.applyValue(
				parameter.image?.scale, WatermarkImage.getScaleDefault()
			) / 100 * previewScale);
			imageStyle.transform = "rotate(" + parameter.angle + "deg) translate(-50%, -50%)";
		}

		return (
			<img hidden={parameter.mode !== WatermarkMode.IMAGE} ref={this.imagePreview}
				 src={"data:" + this.state.imageFile.mimetype + ";base64," + this.state.imageFile.value}
				 alt={Translate.value("watermark.preview.alt", {ns: "dialog"})} style={imageStyle}/>
		);
	}

	private renderPreview(): ReactNode {
		let selected: PortalDocumentInterface | undefined = this.props.documentSelection.get().pop();
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(selected) || !FunctionHelper.exists(parameter)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(selected!.id!);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let page: PagePages | undefined = portalDocument.getPage(documentSettings.activeViewer.currentPage);
		let previewScale: number = this.state.previewZoomValue / 100;
		let rotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);
		let pageWidth: number = 0;
		let pageHeight: number = 0;
		let position: WatermarkPositionMode | undefined = parameter?.mode === WatermarkMode.IMAGE ?
			FunctionHelper.applyValue(parameter?.image?.position?.position, WatermarkPosition.getPositionDefault()) :
			FunctionHelper.applyValue(parameter?.text?.position?.position, WatermarkPosition.getPositionDefault());

		let selectionLeft: number | undefined = parameter?.mode === WatermarkMode.IMAGE ?
			parameter?.image?.position?.x : parameter?.text?.position?.x;
		let selectionTop: number | undefined = parameter?.mode === WatermarkMode.IMAGE ?
			parameter?.image?.position?.y : parameter?.text?.position?.y;
		let selectionWidth: number | undefined = parameter?.mode === WatermarkMode.IMAGE ?
			parameter?.image?.position?.width : parameter?.text?.position?.width;
		let selectionHeight: number | undefined = parameter?.mode === WatermarkMode.IMAGE ?
			parameter?.image?.position?.height : parameter?.text?.position?.height;

		switch (rotation) {
			case 0:
			case 2:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, MetadataRectangle.getWidthDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, MetadataRectangle.getHeightDefault()
				);
				break;
			case 1:
			case 3:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, MetadataRectangle.getHeightDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, MetadataRectangle.getWidthDefault()
				);
				break;
		}

		pageWidth = Math.round(
			UnitHelper.unitToPx(pageWidth, LengthUnit.POINTS, this.props.portalResolution) * previewScale
		);
		pageHeight = Math.round(
			UnitHelper.unitToPx(pageHeight, LengthUnit.POINTS, this.props.portalResolution) * previewScale
		);

		return (
			<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left">
				<div className="dialog-preview" style={{
					maxWidth: pageWidth <= 0 ? "100%" : pageWidth,
					maxHeight: pageHeight <= 0 ? "100%" : pageHeight
				}}>
					<PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PAGE}
							   portalDocument={Immutable.from(selected!)}
							   currentPage={documentSettings.activeViewer.currentPage}
							   updatePreviewState={this.setState.bind(this)}/>
					<div className="preview-layer">
						{this.renderImagePreview()}
						{this.renderTextPreview()}
						{position === WatermarkPositionMode.Custom &&
                            <div className="custom-selection" style={{
								left: UnitHelper.unitToPx(
									FunctionHelper.applyValue(selectionLeft, WatermarkPosition.getXDefault()),
									LengthUnit.MILLIMETER, this.props.portalResolution
								) * previewScale,
								top: UnitHelper.unitToPx(
									FunctionHelper.applyValue(selectionTop, WatermarkPosition.getYDefault()),
									LengthUnit.MILLIMETER, this.props.portalResolution
								) * previewScale,
								width: UnitHelper.unitToPx(
									FunctionHelper.applyValue(selectionWidth, WatermarkPosition.getWidthDefault()),
									LengthUnit.MILLIMETER, this.props.portalResolution
								) * previewScale,
								height: UnitHelper.unitToPx(
									FunctionHelper.applyValue(selectionHeight, WatermarkPosition.getHeightDefault()),
									LengthUnit.MILLIMETER, this.props.portalResolution
								) * previewScale
							}}/>
						}
					</div>
				</div>
			</div>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;
		let errors: Map<WatermarkDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let position: WatermarkPositionMode = parameter?.mode === WatermarkMode.IMAGE ?
			FunctionHelper.applyValue(parameter?.image?.position?.position, WatermarkPosition.getPositionDefault()) :
			FunctionHelper.applyValue(parameter?.text?.position?.position, WatermarkPosition.getPositionDefault());

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid"
							  hidden={!this.state.fields?.contains(WatermarkDialogFields.MODE)}>
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-mode">
								<Translate {...this.props} data="watermark.type.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-mode"
										  value={FunctionHelper.applyValue(
											  parameter?.mode, PortalToolboxWatermarkWatermark.getModeDefault()
										  )}
										  options={[
											  {
												  label: Translate.value(
													  "watermark.type.text", {ns: "dialog"}
												  ),
												  value: WatermarkMode.TEXT
											  },
											  {
												  label: Translate.value(
													  "watermark.type.image", {ns: "dialog"}
												  ),
												  value: WatermarkMode.IMAGE
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  WatermarkDialogFields.MODE, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
					</fieldset>

					{parameter?.mode === WatermarkMode.TEXT && this.renderTextFields()}
					{parameter?.mode === WatermarkMode.IMAGE && this.renderImageFields()}

					<fieldset className="p-col-12 p-grid"
							  hidden={
								  !this.state.fields?.contains(WatermarkDialogFields.POSITION) &&
								  !this.state.fields?.contains(WatermarkDialogFields.MARGIN_TOP) &&
								  !this.state.fields?.contains(WatermarkDialogFields.MARGIN_LEFT) &&
								  !this.state.fields?.contains(WatermarkDialogFields.ROTATION)
							  }>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							position === WatermarkPositionMode.Custom ||
							!this.state.fields?.contains(WatermarkDialogFields.POSITION)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-position">
								<Translate {...this.props} data="watermark.display.position" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<PositionInput value={position as any}
											   onChange={(value) => this.onChange(
												   WatermarkDialogFields.POSITION, value
											   )}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={
								 position === WatermarkPositionMode.Custom ||
								 (
									 !this.state.fields?.contains(WatermarkDialogFields.MARGIN_LEFT) &&
									 !this.state.fields?.contains(WatermarkDialogFields.MARGIN_TOP)
								 )
							 }>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-left"
								   hidden={!this.state.fields?.contains(WatermarkDialogFields.MARGIN_LEFT)}>
								<Translate {...this.props} data="watermark.display.offset.horizontal"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-left" step={1} maxFractionDigits={2}
												 minFractionDigits={2} showButtons={true} suffix={" " + this.state.unit}
												 useGrouping={false}
												 value={UnitHelper.unitToUnit(
													 parameter?.mode === WatermarkMode.IMAGE ?
														 FunctionHelper.applyValue(
															 parameter?.image?.position?.x,
															 WatermarkPosition.getXDefault()
														 ) :
														 FunctionHelper.applyValue(
															 parameter?.text?.position?.x,
															 WatermarkPosition.getXDefault()
														 ),
													 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
												 )}
												 min={WatermarkPosition.getXMin()}
												 onChange={(e) => this.onChange(
													 WatermarkDialogFields.MARGIN_LEFT, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													WatermarkDialogFields.MARGIN_LEFT,
													WatermarkPosition.getXDefault()
												)}/>
									</div>
								</div>
							</div>

							<label className="p-col-12 p-sm-5" htmlFor="control-margin-top"
								   hidden={!this.state.fields?.contains(WatermarkDialogFields.MARGIN_TOP)}>
								<Translate {...this.props} data="watermark.display.offset.vertical" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
												 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
												 useGrouping={false} min={WatermarkPosition.getYMin()}
												 value={UnitHelper.unitToUnit(
													 parameter?.mode === WatermarkMode.IMAGE ?
														 FunctionHelper.applyValue(
															 parameter?.image?.position?.y,
															 WatermarkPosition.getYDefault()
														 ) :
														 FunctionHelper.applyValue(
															 parameter?.text?.position?.y,
															 WatermarkPosition.getYDefault()
														 ),
													 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
												 )}
												 onChange={(e) => this.onChange(
													 WatermarkDialogFields.MARGIN_TOP, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													WatermarkDialogFields.MARGIN_TOP,
													WatermarkPosition.getYDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>
						<div className="p-col-12" hidden={position === WatermarkPositionMode.Custom}>
							<Helper {...this.props}>
								<Translate {...this.props} data={"dialog.elements.position"} namespace={"help"}
										   markdown={true}/>
							</Helper>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							position !== WatermarkPositionMode.Custom ||
							!this.state.fields?.contains(WatermarkDialogFields.ASPECT_RATIO)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-aspect-ratio"/>
							<div className="p-col-12 p-sm-7 switch-container">
								<InputSwitch inputId="control-aspect-ratio" checked={
									parameter?.mode === WatermarkMode.IMAGE ?
										FunctionHelper.applyValue(parameter?.image?.position?.aspectRatio,
											WatermarkPosition.getAspectRatioDefault()
										) :
										FunctionHelper.applyValue(parameter?.text?.position?.aspectRatio,
											WatermarkPosition.getAspectRatioDefault()
										)
								} onChange={(e) => this.onChange(
									WatermarkDialogFields.ASPECT_RATIO, e.value
								)}/>
								<label className="switch-label" htmlFor="control-aspect-ratio">
									<Translate {...this.props} data="watermark.display.aspect_ratio.label"
											   namespace="dialog"/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(WatermarkDialogFields.ROTATION)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-rotation">
								<Translate {...this.props} data="watermark.display.rotation" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<DropdownNumber inputId="control-rotation" showButtons={true}
												value={FunctionHelper.applyValue(
													parameter?.angle, ToolboxWatermarkWatermark.getAngleDefault()
												)}
												onChange={(e) => this.onChange(
													WatermarkDialogFields.ROTATION, e.value
												)}
												appendTo={document.getElementById("root")} suffix=" °"
												min={ToolboxWatermarkWatermark.getAngleMin()}
												max={ToolboxWatermarkWatermark.getAngleMax()}
												options={[
													{label: "0", value: 0},
													{label: "45", value: 45},
													{label: "90", value: 90},
													{label: "135", value: 135},
													{label: "180", value: 180},
													{label: "225", value: 225},
													{label: "270", value: 270},
													{label: "315", value: 315}
												]}/>
							</div>
						</div>
					</fieldset>

					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(WatermarkDialogFields.PAGERANGE)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
								<Translate {...this.props} data="watermark.pages.label" namespace="dialog"/>
								<Info {...this.props} data="watermark.pages.info" options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-7">
								<PageRange inputId="control-pagerange"
										   value={FunctionHelper.applyValue(
											   parameter?.pages, ToolboxWatermarkWatermark.getPagesDefault()
										   )}
										   className={errors.has(WatermarkDialogFields.PAGERANGE) ? " p-invalid" : ""}
										   onChange={(e) => this.onChange(
											   WatermarkDialogFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")}
										   addon={Translate.value(
											   "inputs.pagerange.of", {pages: this.state.pages}
										   )}
										   tooltip={errors.has(WatermarkDialogFields.PAGERANGE) ? Translate.value(
											   errors.get(WatermarkDialogFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {
												   label: Translate.value("inputs.pagerange.all"),
												   value: PAGERANGE_ALL
											   },
											   {
												   label: Translate.value("inputs.pagerange.current"),
												   value: this.state.currentPage
											   }
										   ]}/>
								{this.renderFieldError(errors.has(WatermarkDialogFields.PAGERANGE) ?
									Translate.value(
										errors.get(WatermarkDialogFields.PAGERANGE)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	public renderImageFields(): ReactNode {
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let position: WatermarkPositionMode = FunctionHelper.applyValue(
			parameter?.image?.position?.position, WatermarkPosition.getPositionDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.IMAGE_FILE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-image-file">
							<Translate {...this.props} data="watermark.image.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<FileInput inputId="control-image-file"
									   value={FunctionHelper.applyValue(parameter?.image?.data?.value, "")}
									   filename={this.state.imageFile.name} accept="image/gif, image/png, image/jpeg"
									   onChange={(files) => this.onChange(
										   WatermarkDialogFields.IMAGE_FILE, files[0]
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.IMAGE_OPACITY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-image-opacity">
							<Translate {...this.props} data="watermark.image.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-image-opacity" suffix=" %" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.text?.font?.opacity, WatermarkFont.getOpacityDefault()
										 )}
										 min={WatermarkFont.getOpacityMin()} max={WatermarkFont.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 WatermarkDialogFields.IMAGE_OPACITY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 position === WatermarkPositionMode.Custom ||
							 !this.state.fields?.contains(WatermarkDialogFields.IMAGE_SCALE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-image-scale">
							<Translate {...this.props} data="watermark.image.scale" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-image-scale" showButtons={true} suffix=" %"
											value={FunctionHelper.applyValue(
												parameter?.image?.scale, WatermarkImage.getScaleDefault()
											)}
											onChange={(e) => this.onChange(
												WatermarkDialogFields.IMAGE_SCALE, e.value
											)}
											appendTo={document.getElementById("root")}
											min={WatermarkImage.getScaleMin()}
											options={[
												{label: "10", value: 10},
												{label: "25", value: 25},
												{label: "50", value: 50},
												{label: "75", value: 75},
												{label: "100", value: 100},
												{label: "125", value: 125},
												{label: "150", value: 150},
												{label: "200", value: 200},
												{label: "400", value: 400}
											]}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderTextFields(): ReactNode {
		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;
		let errors: Map<WatermarkDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let position: WatermarkPositionMode = FunctionHelper.applyValue(
			parameter?.text?.position?.position, WatermarkPosition.getPositionDefault()
		);

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(WatermarkDialogFields.MODE)}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.CONTENT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-content">
							<Translate {...this.props} data="watermark.text.content" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-text-content"
									   value={FunctionHelper.applyValue(
										   parameter?.text?.text, WatermarkText.getTextDefault()
									   )}
									   onInput={(e) => this.onChange(
										   WatermarkDialogFields.CONTENT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-font">
							<Translate {...this.props} data="watermark.text.font" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownFilter id="control-text-font" options={fonts}
											appendTo={document.getElementById("root")}
											value={FunctionHelper.applyValue(
												parameter?.text?.font?.name, WatermarkFont.getNameDefault()
											)}
											onChange={(e) => this.onChange(
												WatermarkDialogFields.FONT_FAMILY, e.value
											)}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 position === WatermarkPositionMode.Custom ||
							 !this.state.fields?.contains(WatermarkDialogFields.FONT_SIZE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-size">
							<Translate {...this.props} data="watermark.text.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-text-size" showButtons={true}
											suffix={" " + LengthUnit.POINTS}
											value={FunctionHelper.applyValue(
												parameter?.text?.font?.size, WatermarkFont.getSizeDefault()
											)}
											min={WatermarkFont.getSizeMin()} max={WatermarkFont.getSizeMax()}
											onChange={(e) => this.onChange(
												WatermarkDialogFields.FONT_SIZE, e.value
											)}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-color">
							<Translate {...this.props} data="watermark.text.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(WatermarkDialogFields.FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(WatermarkDialogFields.FONT_COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(WatermarkDialogFields.FONT_COLOR) ? Translate.value(
											   errors.get(WatermarkDialogFields.FONT_COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   parameter?.text?.font?.color, WatermarkFont.getColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   WatermarkDialogFields.FONT_COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-text-color" format="hex" className="p-inputgroup-addon"
											 defaultColor={
												 WatermarkFont.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 parameter?.text?.font?.color, WatermarkFont.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 WatermarkDialogFields.FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}
								/>
							</div>
							{this.renderFieldError(errors.has(WatermarkDialogFields.FONT_COLOR) ?
								Translate.value(
									errors.get(WatermarkDialogFields.FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(WatermarkDialogFields.FONT_OPACITY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-opacity">
							<Translate {...this.props} data="watermark.text.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-text-opacity" suffix=" %" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.text?.font?.opacity, WatermarkFont.getOpacityDefault()
										 )}
										 min={WatermarkFont.getOpacityMin()} max={WatermarkFont.getOpacityMax()}
										 onChange={(e) => this.onChange(WatermarkDialogFields.FONT_OPACITY, e.value)}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: WatermarkDialogFields, value: any): void {
		let parameter: PortalToolboxWatermarkWatermark = this.state.parameter!.data;
		let errors: Map<WatermarkDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case WatermarkDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
			case WatermarkDialogFields.MODE:
				if (value === WatermarkMode.TEXT) {
					this.onChange(WatermarkDialogFields.FONT_COLOR, parameter.text?.font?.color);
				} else if (errors.has(WatermarkDialogFields.FONT_COLOR)) {
					errors.delete(WatermarkDialogFields.FONT_COLOR);
				}

				parameter.mode = value;
				break;
			case WatermarkDialogFields.CONTENT:
				parameter.text!.text = value;
				break;
			case WatermarkDialogFields.FONT_FAMILY:
				parameter.text!.font!.name = value;
				break;
			case WatermarkDialogFields.FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = WatermarkFont.getSizeDefault();
				}

				parameter.text!.font!.size = value;
				break;
			case WatermarkDialogFields.FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.text!.font!.color = value;
				break;
			case WatermarkDialogFields.FONT_OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = WatermarkFont.getOpacityDefault();
				}

				parameter.text!.font!.opacity = value;
				break;
			case WatermarkDialogFields.IMAGE_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: PortalWatermarkFileData = PortalWatermarkFileData.fromJson({
					...file
				});
				parameter.image!.data = fileDataType;

				let preRenderedImage: HTMLImageElement = new Image();
				preRenderedImage.onload = () => {
					this.setState({
						imageFile: file,
						imageWidth: preRenderedImage.naturalWidth,
						imageHeight: preRenderedImage.naturalHeight,
						parameter: this.state.parameter
					});
				};
				preRenderedImage.src = "data:" + file.mimetype + ";base64," + file.value;
				return;
			case WatermarkDialogFields.IMAGE_OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = WatermarkImage.getOpacityDefault();
				}

				parameter.image!.opacity = value;
				break;
			case WatermarkDialogFields.IMAGE_SCALE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = WatermarkImage.getScaleDefault();
				}

				parameter.image!.scale = value;
				break;
			case WatermarkDialogFields.ROTATION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxWatermarkWatermark.getAngleDefault();
				}

				parameter.angle = value;
				break;
			case WatermarkDialogFields.MARGIN_LEFT:
				if (parameter.mode === WatermarkMode.IMAGE) {
					parameter.image!.position!.x = UnitHelper.unitToUnit(
						value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
					);
				} else {
					parameter.text!.position!.x = UnitHelper.unitToUnit(
						value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
					);
				}
				break;
			case WatermarkDialogFields.MARGIN_TOP:
				if (parameter.mode === WatermarkMode.IMAGE) {
					parameter.image!.position!.y = UnitHelper.unitToUnit(
						value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
					);
				} else {
					parameter.text!.position!.y = UnitHelper.unitToUnit(
						value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
					);
				}
				break;
			case WatermarkDialogFields.POSITION:
				if (parameter.mode === WatermarkMode.IMAGE) {
					parameter.image!.position!.position = value;
				} else {
					parameter.text!.position!.position = value;
				}
				break;
			case WatermarkDialogFields.ASPECT_RATIO:
				if (parameter.mode === WatermarkMode.IMAGE) {
					parameter.image!.position!.aspectRatio = value;
				} else {
					parameter.text!.position!.aspectRatio = value;
				}
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxWatermarkWatermark | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		let selectedFont: string | undefined = parameter?.text?.font?.name;
		let fonts: Array<string> = FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		);
		if (!FunctionHelper.exists(selectedFont) || (fonts.length > 0 && fonts.indexOf(selectedFont!) === -1)) {
			parameter!.text!.font!.name = fonts[0];
		}

		if (parameter?.text?.text === "") {
			parameter!.text.text = Translate.value("watermark.text.example", {ns: "dialog"});
		}

		if (parameter?.text?.font?.name === "") {
			let fonts: Array<string> = FunctionHelper.applyValue(
				this.props.context.info.environment?.fonts, Environment.getFontsDefault()
			);
			parameter!.text.font.name = fonts[0];
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage,
			previewZoomValue: 0,
			unit: userSettings.unit
		}, () => {
			let fileDataType: PortalWatermarkFileData | undefined =
				parameter?.image?.data as PortalWatermarkFileData;
			if (fileDataType.value !== "" && fileDataType.mimetype !== "" && fileDataType.name !== "") {
				this.onChange(WatermarkDialogFields.IMAGE_FILE, {
					name: fileDataType.name!,
					mimetype: fileDataType.mimetype!,
					value: fileDataType.value!
				});
			}
		});
	};

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState({
			renderPreview: false
		}, () => super.hide());
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-watermark small"}/>
					<Translate {...this.props} data="watermark.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-grid">
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<div className="p-col-12 p-grid">
								<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
										disabled={this.state.submitted} className="reset-button"
										onClick={() => this.resetParameter()}/>
								<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
								{this.state.submitted ?
									<Button label={Translate.value("buttons.close")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/> :
									<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/>
								}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left-hidden"></div>
			</div>
		);
	}

	protected async submit(): Promise<void> {
		let watermarkParameter: PortalToolboxWatermarkWatermark = this.state.parameter!.data.clone();
		await super.submit();

		let position: WatermarkPositionMode | undefined = watermarkParameter?.mode === WatermarkMode.IMAGE ?
			FunctionHelper.applyValue(watermarkParameter?.image?.position?.position, WatermarkPosition.getPositionDefault()) :
			FunctionHelper.applyValue(watermarkParameter?.text?.position?.position, WatermarkPosition.getPositionDefault());

		if (watermarkParameter.mode === WatermarkMode.IMAGE) {
			if (position === WatermarkPositionMode.Custom) {
				watermarkParameter.image!.scale = undefined
			}

			watermarkParameter.text = undefined;
		} else {
			if (position === WatermarkPositionMode.Custom) {
				watermarkParameter.text!.font!.size = undefined
			}

			watermarkParameter.image = undefined;
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			watermark: watermarkParameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});

				if (this.state.parameter?.data.image?.position?.position === WatermarkPositionMode.Custom) {
					this.state.parameter!.data.text!.position = new WatermarkPosition({});
					this.state.parameter!.data.image!.position = new WatermarkPosition({});

					this.setState({
						parameter: this.state.parameter
					}, async () => await super.submit());
				}
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.watermark", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.resetMouseMode(documentManager.getSelection());
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.WATERMARK;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxWatermarkWatermark> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const WatermarkDialog = React.memo(WatermarkDialogDefinition,
	(prevProps: Readonly<WatermarkDialogProperties>, nextProps: Readonly<WatermarkDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default WatermarkDialog;